import Query from '@/services/query/QueryServiceFabrick'
import { Status, Traffic } from '@/API'
import VehicleModel from '@/services/models/Vehicle'
import DriverModel from '@/services/models/Driver'
import TrafficModel from '@/services/models/Traffic'
import StatusModel from '@/services/models/Status'
import DeliveryPlanModel from '@/services/models/DeliveryPlan'
import VehicleInfoType from '@/types/vehicleInfo'
import VehicleType from '@/types/vehicle'
import deliveryPlanInfoType from '@/types/deliveryPlanInfo'
import DriverType from '@/types/driverInfo'
import {
  ENGINE_OFF_THRESHOLD_MINUTES,
  workStatus,
  IGNORE_STATUS,
  NONE_DEPARTMENT,
} from '@/services/const'

// レースコンディションが発生した時に、古い検索処理を中止するためのID
let currentGetVehicleInformationsRequestId = 0

export const getVehicleInformations = async (
  callback: Function = () => {},
  operationOfficeIds: number[] = []
) => {
  const vehicles: VehicleType[] = []
  let drivers: DriverType[] = []

  const vehicleDetailPromises: (() => Promise<void>)[] = []

  const requestId = ++currentGetVehicleInformationsRequestId

  const vehiclePromise = getAllVehicles(
    (data: VehicleType[]) => {
      if (requestId !== currentGetVehicleInformationsRequestId) {
        return
      }
      data.forEach((vehicle) => {
        vehicles.push(vehicle)
        vehicleDetailPromises.push(() => setRawDatas(vehicle))
      })
      callback(vehicles)
    },
    operationOfficeIds,
    requestId
  )
  const driverPromise = getAllDrivers((data: DriverType[]) => {
    if (requestId !== currentGetVehicleInformationsRequestId) {
      return
    }
    drivers = data
    mergeDrivers(vehicles, drivers)
    callback(vehicles)
  }, operationOfficeIds)

  await Promise.all([vehiclePromise, driverPromise])

  if (requestId !== currentGetVehicleInformationsRequestId) {
    return
  }

  mergeDrivers(vehicles, drivers)

  const dpIdsWithoutDriver = vehicles
    .filter((v) => !v.driver_name && v.delivery_plan_id)
    .map((v) => v.delivery_plan_id)

  const dpFetchPromise =
    dpIdsWithoutDriver.length > 0
      ? DeliveryPlanModel.selectByDeliveryPlanIds(dpIdsWithoutDriver)
      : Promise.resolve(null)

  const VEHICLE_DETAIL_CONCURRENCY = 10
  const [, dpInfos] = await Promise.all([
    runWithConcurrencyLimit(vehicleDetailPromises, VEHICLE_DETAIL_CONCURRENCY),
    dpFetchPromise,
  ])

  // 権限範囲の問題でgetAllDrivers()で取得できなかったドライバー情報があれば付与
  if (dpInfos) {
    const dpInfoMap = new Map<string, deliveryPlanInfoType>()
    dpInfos.data.delivery_plans.forEach((d) => {
      dpInfoMap.set(d.delivery_plan_id, d)
    })
    vehicles.forEach((v) => {
      if (!v.driver_name && v.delivery_plan_id) {
        const dpInfo = dpInfoMap.get(v.delivery_plan_id)
        if (dpInfo?.driver_name) {
          v.driver_name = dpInfo.driver_name
          v.phone_number = dpInfo.driver_telephone_number
        }
      }
    })
  }

  callback(vehicles)
  return Promise.resolve()
}

const getAllVehicles = async (
  callback: Function = () => {},
  operationOfficeIds: number[] = [],
  requestId: number
) => {
  let totalPages = 1

  for (let i = 1; i <= totalPages; i++) {
    if (requestId !== currentGetVehicleInformationsRequestId) {
      return
    }
    const res = await VehicleModel.getPerPage(i, operationOfficeIds)
    totalPages = res.totalPages ?? 1
    const data = convertVehicleInforToVehicle(res.data ?? [])
    callback(data)
  }
  return Promise.resolve()
}

const convertVehicleInforToVehicle = (vehicles: VehicleInfoType[]) => {
  return vehicles.map((vehicle: VehicleInfoType) => {
    return {
      // vehicle
      device_id: vehicle.serial_no,
      vehicle_id: vehicle.id,
      registration_number: vehicle.registration_number,
      driver_id: vehicle.driver_id,
      delivery_plan_id: vehicle.latest_delivery_plan_id,
      leaving_warehouse_date_time: vehicle.leaving_warehouse_date_time,
      operation_office_id: vehicle.operation_office_id,
      department_id: vehicle.department_id ?? NONE_DEPARTMENT.id,
      maker_id: vehicle.maker_id,

      // driver
      driver_name: undefined,
      phone_number: undefined,

      position: undefined,
      status: workStatus.WORK_STATUS_NOTHING,
      last_address_name: undefined,
      latest_status_time: undefined,
      latest_driving_time: undefined,
      in_service: undefined,
      engine_on: undefined,
      last_positions: [],
      latest_traffic_time: undefined,
      is_loaded: false,
      is_loaded_every_attributes: false,
      is_on_highway: false,
    }
  })
}

const getAllDrivers = async (
  callback: Function = () => {},
  operationOfficeIds: number[] = []
) => {
  const res = await DriverModel.selectAll({ operationOfficeIds })
  callback(res.data ?? [])
  return Promise.resolve()
}

const mergeDrivers = (vehicles: VehicleType[], drivers: DriverType[]) => {
  vehicles.forEach((vehicle: VehicleType) => {
    if (vehicle.driver_name) {
      return
    }
    drivers.forEach((driver: DriverType) => {
      if (
        vehicle.operation_office_id == driver.operation_office_id &&
        vehicle.driver_id == driver.driver_pin
      ) {
        vehicle.driver_name = driver.name
        vehicle.phone_number = driver.telephone_number
      }
    })
  })
}

const setRawDatas = async (vehicle: VehicleType) => {
  const [statuses, traffics] = await Promise.all([
    getStatuses(vehicle),
    getTraffics(vehicle),
  ])
  vehicle.is_loaded_every_attributes = true
  await Promise.all([
    setStatus(vehicle, statuses, traffics),
    setTraffic(vehicle, statuses, traffics),
  ])
}

const getStatuses = async (vehicle: VehicleType): Promise<Status[]> => {
  return StatusModel.fetchBySerialNumber({
    serial_number: vehicle.device_id,
    max: 100,
    orderby: 'DESC',
  })
}

const getTraffics = async (vehicle: VehicleType): Promise<Traffic[]> => {
  return TrafficModel.fetchBySerialNumber({
    serial_number: vehicle.device_id,
    max: (15 * 60) / 5,
    orderby: 'DESC',
  })
}

const setTraffic = async (
  vehicle: VehicleType,
  statuses: Status[],
  traffics: Traffic[]
) => {
  if (!traffics[0]) return

  await setLatestTraffic(vehicle, traffics)
}

const setStatus = async (
  vehicle: VehicleType,
  statuses: Status[],
  traffics: Traffic[]
): Promise<void> => {
  if (!statuses[0]) return

  let latestStatus: Status | null = null
  statuses.forEach((st) => {
    const work = StatusModel.convertStatus(st)
    if (!latestStatus && IGNORE_STATUS.indexOf(work) < 0) {
      latestStatus = st
    }
  })
  if (!latestStatus) {
    latestStatus = statuses[0]
  }
  setInService(vehicle, latestStatus)
  setIsLoaded(vehicle, statuses)

  let status: workStatus
  if (!latestStatus) {
    status = workStatus.WORK_STATUS_NOTHING
  }
  if (!vehicle.in_service) {
    status = workStatus.WORK_STATUS_RETURNING_WAREHOUSE
  }
  const latestDrivingTraffic = await getLatestDrivingTraffic(traffics)
  /////////////////////// vehicleStatusByStatusAndLatestTraffic
  let work = workStatus.WORK_STATUS_LEAVING_WAREHOUSE
  if (latestStatus) {
    work = StatusModel.convertStatus(latestStatus)
  }
  const latestDrivingTime = latestDrivingTraffic?.tm
    ? latestDrivingTraffic.tm * 1000
    : 0
  const latestStatusTime = (latestStatus?.time ?? 0) * 1000
  const current_time = new Date()
  status = StatusModel.investigateVehicleStatus(
    work,
    latestStatusTime,
    latestDrivingTime,
    current_time
  )
  ;(vehicle.status = status
    ? StatusModel.convertWorkStatusToVehicleMapStatus(status)
    : workStatus.WORK_STATUS_NOTHING),
    (vehicle.latest_status_time = new Date(
      latestStatus?.created_at ?? 0
    ).getTime())
  vehicle.is_loaded = await Query().isLoaded(vehicle.device_id)
  const latestRoadStatus = getLatestRoadStatus(statuses)
  vehicle.is_on_highway = latestRoadStatus === workStatus.WORK_STATUS_HIGH_WAY
  return Promise.resolve()
}

const setInService = async (
  vehicle: VehicleType,
  latestStatus: Status
): Promise<void> => {
  let vehicleStatus = undefined
  let inService = true
  vehicleStatus = StatusModel.convertStatus(<Status>latestStatus)
  if (vehicleStatus == workStatus.WORK_STATUS_RETURNING_WAREHOUSE) {
    inService = false
  }
  vehicle.in_service = inService
}

const setLatestTraffic = async (
  vehicle: VehicleType,
  traffics: Traffic[]
): Promise<void> => {
  const latestTraffic = traffics[0]
  vehicle.latest_traffic_time = latestTraffic?.created_at
    ? new Date(latestTraffic.created_at).getTime()
    : 0
  vehicle.latest_driving_time = vehicle?.latest_traffic_time

  if (latestTraffic) {
    const latestPosition = TrafficModel.convertTrafficsToPositions([
      latestTraffic,
    ])[0]
    vehicle.position = latestPosition

    const beforeTenMinutes = ENGINE_OFF_THRESHOLD_MINUTES * 60 * 1000
    const engineOn =
      vehicle?.latest_traffic_time > new Date().getTime() - beforeTenMinutes
    vehicle.engine_on = engineOn
  }
  return Promise.resolve()
}

const setIsLoaded = async (
  vehicle: VehicleType,
  statuses: Status[]
): Promise<void> => {
  const latestDeliveryPlanStatuses = findLatestDeliveryPlanStatuses(statuses)
  vehicle.is_loaded = wasCarLoadedAndIsCarStillLoaded(
    latestDeliveryPlanStatuses
  )
  return Promise.resolve()
}

const findLatestDeliveryPlanStatuses = (arStatuses: Status[]): Status[] => {
  const startIndex = findStatusIndex(
    arStatuses,
    workStatus.WORK_STATUS_LEAVING_WAREHOUSE
  )
  if (startIndex > 0) {
    return arStatuses.slice(0, startIndex + 1)
  }
  return []
}
const wasCarLoadedAndIsCarStillLoaded = (arStatuses: Status[]): boolean => {
  const carWasLoaded = wasCarLoaded(arStatuses)
  if (carWasLoaded) {
    return isCarStillLoaded(arStatuses)
  }
  return false
}

const isCarStillLoaded = (arStatuses: Status[]): boolean => {
  const loadedCarStatusIndex = findStatusIndex(
    arStatuses,
    workStatus.WORK_STATUS_ACTUAL_CAR
  )
  const ar_statuses_since_loaded_car_status = arStatuses.slice(
    0,
    loadedCarStatusIndex + 1
  )
  const emptyCarStatusIndex = findStatusIndex(
    ar_statuses_since_loaded_car_status,
    workStatus.WORK_STATUS_EMPTY_CAR
  )
  return emptyCarStatusIndex === -1
}

const wasCarLoaded = (arStatuses: Status[]): boolean => {
  const loadedCarStatusIndex = findStatusIndex(
    arStatuses,
    workStatus.WORK_STATUS_ACTUAL_CAR
  )
  return loadedCarStatusIndex >= 0
}

const findStatusIndex = (
  arStatuses: Status[],
  needStatus: workStatus
): number => {
  return arStatuses.findIndex((status) => {
    return StatusModel.convertStatus(status) == needStatus
  })
}
const getLatestDrivingTraffic = async (
  traffics: Traffic[]
): Promise<Traffic> => {
  // 約15分のデータを取得
  return new Promise((resolve) => {
    traffics.forEach((traffic: Traffic) => {
      if (traffic.sp > 0) {
        resolve(traffic)
      }
    })
    resolve(traffics[traffics.length - 1])
  })
}
const runWithConcurrencyLimit = async (
  tasks: (() => Promise<void>)[],
  limit: number
): Promise<void> => {
  for (let i = 0; i < tasks.length; i += limit) {
    await Promise.all(tasks.slice(i, i + limit).map((task) => task()))
  }
}

const getLatestRoadStatus = (arStatuses: Status[]): workStatus | null => {
  for (const st of arStatuses) {
    const wst = StatusModel.convertStatus(st)
    if (
      wst === workStatus.WORK_STATUS_HIGH_WAY ||
      wst === workStatus.WORK_STATUS_GENERAL_WAY ||
      wst === workStatus.WORK_STATUS_LEAVING_WAREHOUSE ||
      wst === workStatus.WORK_STATUS_RETURNING_WAREHOUSE
    ) {
      return wst
    }
  }
  return null
}
